import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAbility } from '@casl/react';
import { CircularProgress } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { Box } from '@mui/system';
import { useQueryClient } from '@tanstack/react-query';

import usePearsQuery from '@components/common/hooks/api/usePearsQuery';
import useCurriculaQuery from '@components/common/hooks/api/useCurriculaQuery';
import useStaffUsersQuery from '@components/common/hooks/api/useStaffUsersQuery';
import { axiosRequest } from '@lib/axios';
import { AbilityContext, Subjects } from '@lib/permissions';
import { prepareAnswers, scrollToAlert } from '@lib/utils';
import { PageContext } from '@lib/contexts/pageContext';
import UserContext from '@lib/contexts/userContext';
import EventForm from '../EventForm';
import useFormQuery from '@components/common/hooks/api/useFormQuery';
import DataLoadingError from '@components/common/DataLoadingError';

const CreateEvent = () => {
  const {
    setPageMessage,
    setPageErrorMessage,
  } = useContext(PageContext);
  const { userState: { userRecord } } = useContext(UserContext);

  const ability = useAbility(AbilityContext);

  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const [submitting, setSubmitting] = useState(false);
  const valuesSetState = useState(null);
  // const [, setValuesSet] = valuesSetState;

  const { data: formQuestions, ...formQuery } = useFormQuery('EVENT');

  const form = useForm({
    mode: 'onSubmit',
    defaultValues: {
      point: null,
      event_type: null,
      curriculum: null,
      curriculum_name: null,
      created_by: userRecord?.email,
      event_name: null,
      event_partner: null,
      main_office: null,
      is_test: null,
      description: 'Event description coming in a future phase.',
    }
  });

  const staffUsersQuery = useStaffUsersQuery();
  const pearsQuery = usePearsQuery();
  const curriculaQuery = useCurriculaQuery();

  const isLoading = staffUsersQuery.isLoading || pearsQuery.isLoading || curriculaQuery.isLoading || formQuery.isLoading;

  /**
   * Submit handler.
   *
   * @param values
   */
  const onSubmit = async (values) => {
    setPageMessage(null);
    setPageErrorMessage(null);
    setSubmitting(true);

    const answers = prepareAnswers(formQuestions, values);

    const input = Object.keys(values).reduce((acc, key) => {
      const value = values[key];
      switch (key) {
        case 'curriculum':
          if (value?.id) {
            acc[`${key}_id`] = value?.id;
          }
          break;
        case 'event_name':
        case 'curriculum_name':
          acc[key] = value?.trim();
          break;
        case 'created_by':
        case 'event_partner':
        case 'event_type':
        case 'main_office':
        case 'points':
          acc[key] = value?.id || value;
          break;
        case 'is_test':
          acc[key] = values[key] === 'true';
          break;
        default: break;
      }
      return acc;
    }, {
      ...(answers ? { answers } : {}),
    });

    if (ability.cannot('create', Subjects.EVENT)) {
      setSubmitting(false);
      setPageErrorMessage('You do not have permission to create events.');
      scrollToAlert();
      return false;
    }
    // Staff users can only assign events to themselves
    if (input.created_by !== userRecord?.email && ability.cannot('assign', Subjects.EVENT)) {
      setSubmitting(false);
      setPageErrorMessage('You do not have permission to assign events to other staff members.');
      scrollToAlert();
      return false;
    }
    try {
      const result = await axiosRequest({
        method: 'POST',
        url: 'events',
        data: input,
      });
      await queryClient.invalidateQueries({ queryKey: ['events'] });
      const id = result?.id;
      setSubmitting(false);
      navigate(`/events/${id}`, { state: { message: 'Event created' }});
    } catch (error) {
      console.log('Error creating event: ', error);
      setSubmitting(false);
      setPageErrorMessage(['Error creating event.', ...(error?.errors ?? [])]);
      scrollToAlert();
    }
  };

  return (
    <Grid container gap={3} wrap={'nowrap'} columns={10}>
      <Grid xs={10}>
        { isLoading && (
          <Box width="100%" textAlign="center">
            <CircularProgress />
          </Box>
        )}
        { !isLoading && formQuery.isError && (
          <DataLoadingError title={'Failed to load the event form!'} errors={formQuery.error?.errors} />
        )}
        { !isLoading && !formQuery.isError && (
          <EventForm
            form={form}
            event={false}
            questions={formQuestions}
            pearsData={pearsQuery.data}
            staffUsers={staffUsersQuery.data}
            availableCurriculum={curriculaQuery.data}
            submitting={submitting}
            onSubmit={onSubmit}
            valuesSetState={valuesSetState}
          />
        )}
      </Grid>
    </Grid>
  );
};

export default CreateEvent;
